import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  SimpleChanges,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { DateRange } from "app/shared/date/date-range.model";
import {
  startOfISOWeek,
  endOfISOWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
} from "date-fns";
import { Bind } from "primeng/bind";
import { DatePicker } from "primeng/datepicker";
import { FormsModule } from "@angular/forms";
import { ButtonDirective } from "primeng/button";
import { Checkbox } from "primeng/checkbox";

const HOURS_IN_DAY = 24;
const LAST_HOUR = 23;

// What the steppers offer for a side whose time is off: a working day, not the
// 00:00 / 23:59 filler the dates carry while no time has been chosen.
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 17;

@Component({
  selector: "app-date-picker",
  templateUrl: "./date-picker.component.html",
  styleUrls: ["./date-picker.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { "[style.--date-picker-chars]": "valueCharCount" },
  imports: [Bind, DatePicker, FormsModule, ButtonDirective, Checkbox],
})
export class DatePickerComponent implements OnChanges, AfterViewInit {
  @Input() public selectedRange: DateRange;
  public readonly inputClass = input<string>(undefined);
  public readonly opens = input<string>("left");
  public readonly showIcon = input<boolean>(false);
  public readonly showButtonBar = input<boolean>(true);
  public readonly showTime = input<boolean>(false);
  public readonly disabled = input<boolean>(false);
  public readonly placeholder = input<string>("Select date range");
  readonly rangeChange = output<DateRange>();

  public startHour = DEFAULT_START_HOUR;
  public endHour = DEFAULT_END_HOUR;
  public startTimeEnabled = false;
  public endTimeEnabled = false;

  // The parent echoes the range back on every emit, and a range whose ends sit
  // on the day boundary is indistinguishable from "no time chosen" — so the
  // checkboxes are seeded from the first range only and stay user-owned after.
  private timeStateInitialized = false;

  private readonly datePicker = viewChild(DatePicker);

  // PrimeNG reports the first click of a range as [date, null]. Committing that
  // straight away would make the next click open a *new* range instead of
  // closing this one, so a start-only pick is held until the panel closes.
  private pendingSingleDate: Date = null;

  // Widths of the parts the read-only field renders: "dd.mm.yyyy", the " - "
  // between the ends, and the " HH:00" each end gains while its time is on.
  private static readonly DATE_CHARS = 10;
  private static readonly SEPARATOR_CHARS = 3;
  private static readonly TIME_CHARS = 6;

  public get valueCharCount(): number {
    const { DATE_CHARS, SEPARATOR_CHARS, TIME_CHARS } = DatePickerComponent;
    const startChars = DATE_CHARS + (this.startTimeActive ? TIME_CHARS : 0);
    const endChars = DATE_CHARS + (this.endTimeActive ? TIME_CHARS : 0);

    return startChars + SEPARATOR_CHARS + endChars;
  }

  // The checkboxes are seeded from the incoming range and keep their state
  // while showTime is off, but a picker without the time side must behave as a
  // plain date range: no hour in the field, no hour on the emitted dates.
  private get startTimeActive(): boolean {
    return this.showTime() && this.startTimeEnabled;
  }

  private get endTimeActive(): boolean {
    return this.showTime() && this.endTimeEnabled;
  }

  public get formattedStartHour(): string {
    return this.formatHour(this.startHour);
  }

  public get formattedEndHour(): string {
    return this.formatHour(this.endHour);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedRange"]) {
      this.syncTimeStateFromRange();
    }
  }

  // PrimeNG formats both ends of a range through one shared showTime flag, so
  // a side whose checkbox is off would still render its filler hour ("00:00" /
  // "23:59"). The input is read-only and the side controls own the time part,
  // so the per-date formatter is swapped instead of the finished value — that
  // keeps PrimeNG's own field/model writes as the single source of the text.
  public ngAfterViewInit(): void {
    const picker = this.datePicker();

    if (!picker) {
      return;
    }

    // PrimeNG seeds its clock from initTime(this.value) the moment showTime
    // flips on, and on a range picker that value is an array its own
    // implementation cannot read.
    const initTime = picker.initTime.bind(picker);

    picker.initTime = (date: Date): void => {
      initTime(Array.isArray(date) ? (date[0] ?? new Date()) : date);
    };

    const formatDateTime = picker.formatDateTime.bind(picker);

    // showTime is read per call, not here: a picker whose quote type arrives
    // after the view is built starts out with it false and must still pick the
    // swap up when it flips.
    picker.formatDateTime = (date: Date): string => {
      if (!this.showTime() || !picker.isValidDate(date)) {
        return formatDateTime(date);
      }

      const isEnd = Array.isArray(picker.value) && picker.value[1] === date;
      const formatted = picker.formatDate(date, picker.getDateFormat());

      if (isEnd ? !this.endTimeActive : !this.startTimeActive) {
        return formatted;
      }

      return `${formatted} ${this.formatHour(isEnd ? this.endHour : this.startHour)}:00`;
    };

    picker.updateInputfield();
  }

  public onDatePickerChange(value: any) {
    const [start, end] = value || [];
    this.pendingSingleDate = start && !end ? start : null;
    // PrimeNG stamps its own time picker onto whichever end was clicked, so the
    // side controls stay authoritative for the time part.
    this.onDateChange(
      new DateRange(this.applyStartTime(start), this.applyEndTime(end)),
    );
  }

  public onDatePickerClose(): void {
    if (!this.pendingSingleDate) {
      return;
    }

    const single = this.pendingSingleDate;
    this.pendingSingleDate = null;
    this.onDateChange(
      new DateRange(this.applyStartTime(single), this.applyEndTime(single)),
    );
  }

  public incrementStartHour(): void {
    this.startHour = (this.startHour + 1) % HOURS_IN_DAY;
    this.emitCurrentRange();
  }

  public decrementStartHour(): void {
    this.startHour = (this.startHour + LAST_HOUR) % HOURS_IN_DAY;
    this.emitCurrentRange();
  }

  public incrementEndHour(): void {
    this.endHour = (this.endHour + 1) % HOURS_IN_DAY;
    this.emitCurrentRange();
  }

  public decrementEndHour(): void {
    this.endHour = (this.endHour + LAST_HOUR) % HOURS_IN_DAY;
    this.emitCurrentRange();
  }

  public onStartTimeEnabledChange(enabled: boolean): void {
    this.startTimeEnabled = enabled;
    this.emitCurrentRange();
  }

  public onEndTimeEnabledChange(enabled: boolean): void {
    this.endTimeEnabled = enabled;
    this.emitCurrentRange();
  }

  public onDateChange(value: DateRange) {
    if (value.getStart() && value.getEnd()) {
      this.selectedRange = value;
      this.rangeChange.emit(this.selectedRange);
    }
  }

  public selectThisWeek(): void {
    const start = startOfISOWeek(new Date());
    const end = endOfISOWeek(new Date());
    this.onDateChange(new DateRange(start, end));
  }

  public selectThisMonth(): void {
    const start = startOfMonth(new Date());
    const end = endOfMonth(new Date());
    this.onDateChange(new DateRange(start, end));
  }

  public selectLastWeek(): void {
    const start = startOfISOWeek(subWeeks(new Date(), 1));
    const end = endOfISOWeek(subWeeks(new Date(), 1));
    this.onDateChange(new DateRange(start, end));
  }

  public selectLastMonth(): void {
    const start = startOfMonth(subMonths(new Date(), 1));
    const end = endOfMonth(subMonths(new Date(), 1));
    this.onDateChange(new DateRange(start, end));
  }

  private emitCurrentRange(): void {
    if (!this.selectedRange) {
      return;
    }

    this.onDateChange(
      new DateRange(
        this.applyStartTime(this.selectedRange.getStart()),
        this.applyEndTime(this.selectedRange.getEnd()),
      ),
    );

    // The hours live outside the model PrimeNG watches, so a checkbox or step
    // that leaves the dates alone still has to repaint the field.
    this.datePicker()?.updateInputfield();
  }

  private syncTimeStateFromRange(): void {
    const start = this.selectedRange ? this.selectedRange.getStart() : null;
    const end = this.selectedRange ? this.selectedRange.getEnd() : null;

    if (!this.timeStateInitialized && (start || end)) {
      this.startTimeEnabled = !!start && !this.isStartOfDay(start);
      this.endTimeEnabled = !!end && !this.isEndOfDay(end);
      this.timeStateInitialized = true;
    }

    // Read back off the checkbox, not off the date: a side that is off carries
    // filler hours, and hour 0 or 23 on a side that is on is a real choice.
    this.startHour =
      this.startTimeEnabled && start ? start.getHours() : DEFAULT_START_HOUR;
    this.endHour =
      this.endTimeEnabled && end ? end.getHours() : DEFAULT_END_HOUR;
  }

  private applyStartTime(date: Date): Date {
    if (!date) {
      return date;
    }

    const merged = new Date(date);
    merged.setHours(this.startTimeActive ? this.startHour : 0, 0, 0, 0);

    return merged;
  }

  private applyEndTime(date: Date): Date {
    if (!date) {
      return date;
    }

    const merged = new Date(date);

    if (this.endTimeActive) {
      merged.setHours(this.endHour, 0, 0, 0);
    } else {
      merged.setHours(LAST_HOUR, 59, 59, 999);
    }

    return merged;
  }

  private isStartOfDay(date: Date): boolean {
    return date.getHours() === 0 && date.getMinutes() === 0;
  }

  private isEndOfDay(date: Date): boolean {
    return date.getHours() === LAST_HOUR && date.getMinutes() >= 59;
  }

  private formatHour(hour: number): string {
    return hour.toString().padStart(2, "0");
  }
}
